import type { ReactNode } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { colors, radius, shadows } from '@gto/ui/theme/design-tokens'
import { Typography } from '@gto/ui/typography'

import { StatsCard } from '@/components/stats-card'

type DataPoint = {
  label: string
  revenue: number
  commissions: number
}

type TotalCell = {
  /** Stable identity for the cell (used as the React key). */
  id: string
  label: ReactNode
  value: ReactNode
}

type PerformanceChartProps = {
  title: ReactNode
  data: DataPoint[]
  controls?: ReactNode
  /** Optional leading icon for the card header. */
  icon?: ReactNode
  /** Headline totals rendered as a strip inside the card, above the chart. */
  totals?: TotalCell[]
  /** Inner chart height in px. The 240 default keeps existing pages untouched. */
  height?: number
  /**
   * Caption overlaid on the chart when there is no data (or every value is
   * zero). Opt-in so pages that don't pass it keep today's bare axes.
   */
  emptyLabel?: ReactNode
}

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`

type ChartLegendProps = {
  color: string
  label: string
}

const ChartLegend = ({ color, label }: ChartLegendProps) => (
  <div className="flex items-center gap-1.5">
    <span
      className="size-5 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
    <Typography
      color="text.secondary"
      variant="caption"
    >
      {label}
    </Typography>
  </div>
)

export const PerformanceChart = ({
  title,
  data,
  controls,
  icon,
  totals,
  height = 240,
  emptyLabel,
}: PerformanceChartProps) => {
  const chartData = data
  const isEmpty =
    chartData.length === 0 || chartData.every((d) => d.revenue === 0 && d.commissions === 0)

  return (
    <StatsCard
      icon={icon}
      title={title}
      controls={
        <div className="flex items-center gap-4">
          <ChartLegend
            color={colors.primary.main}
            label="Revenue"
          />
          {/* `info` blue, not `success` green: the palette's two greens
              (primary #aafbb2, success #66bb6a) are the same hue family, so the
              legend dot was the only thing separating the two series. */}
          <ChartLegend
            color={colors.info.main}
            label="Commissions"
          />
          {controls}
        </div>
      }
    >
      {totals != null && totals.length > 0 && (
        <div
          className="mb-4 grid gap-[1px] overflow-hidden rounded-sm bg-divider"
          data-tst="perf-totals"
          style={{ gridTemplateColumns: `repeat(${totals.length}, minmax(0, 1fr))` }}
        >
          {totals.map((cell) => (
            <div
              key={cell.id}
              className="flex flex-col gap-0.5 bg-paper-elevation2 px-4 py-3"
            >
              <Typography
                className="font-heading font-semibold leading-120"
                variant="h6"
              >
                {cell.value}
              </Typography>
              <Typography
                // tracking-[0.06em] matches the dashboard-kpi-tile eyebrow.
                // No letterSpacing token (the design tokens are all px-based).
                className="text-xs uppercase tracking-[0.06em]"
                color="text.secondary"
                variant="caption"
              >
                {cell.label}
              </Typography>
            </div>
          ))}
        </div>
      )}

      <div className="relative">
        {emptyLabel != null && isEmpty && (
          <Typography
            className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center"
            color="text.secondary"
            component="div"
            data-tst="chart-empty"
            variant="caption"
          >
            {emptyLabel}
          </Typography>
        )}
        <ResponsiveContainer
          height={height}
          width="100%"
        >
          <AreaChart
            data={chartData}
            margin={{ top: 4, right: 4, bottom: 0, left: -16 }}
          >
            <defs>
              <linearGradient
                id="revenueGradient"
                x1="0"
                x2="0"
                y1="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={colors.primary.main}
                  stopOpacity={0.15}
                />
                <stop
                  offset="95%"
                  stopColor={colors.primary.main}
                  stopOpacity={0}
                />
              </linearGradient>
              <linearGradient
                id="commissionGradient"
                x1="0"
                x2="0"
                y1="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor={colors.info.main}
                  stopOpacity={0.15}
                />
                <stop
                  offset="95%"
                  stopColor={colors.info.main}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              stroke={colors.divider}
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              axisLine={{ stroke: colors.divider }}
              dataKey="label"
              interval="preserveStartEnd"
              tick={{ fill: colors.text.secondary, fontSize: 11 }}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              domain={[0, 'auto']}
              tick={{ fill: colors.text.secondary, fontSize: 11 }}
              tickFormatter={formatCurrency}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: colors.paper.elevation8,
                border: `1px solid ${colors.divider}`,
                borderRadius: Number(radius.sm.replace('px', '')),
                fontSize: 13,
                boxShadow: shadows.elevation8,
              }}
              formatter={(value: number, name: string) => [
                formatCurrency(value),
                name === 'revenue' ? 'Revenue' : 'Commissions',
              ]}
            />
            <Area
              dataKey="revenue"
              fill="url(#revenueGradient)"
              stroke={colors.primary.main}
              strokeWidth={2}
              type="monotone"
            />
            <Area
              dataKey="commissions"
              fill="url(#commissionGradient)"
              stroke={colors.info.main}
              strokeWidth={2}
              type="monotone"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </StatsCard>
  )
}
